import * as React from "react";

import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  addMonths,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";

import Button from "@/components/button";
import { LeftArrow, RightArrow } from "@/components/icons";
import { getShifts } from "@/queries/api/shiftApi";
import { listWorkerShifts } from "@/queries/api/workerShiftApi";
import { getWorkerUpcoming } from "@/queries/api/workerDashboardApi";
import { QueryKeys } from "@/queries/queryKeys";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";
import type { ShiftCard } from "@/utils/assignment-card";
import type { DayCellContentArg, EventApi } from "@fullcalendar/core";
import { assignmentToCard, shiftToScheduleCard, upcomingToCard } from "@/utils/assignment-card";
import { useRole } from "@/utils/hooks/use-role";
import type { Role } from "@/utils/hooks/use-role";

import { ScheduleSidebar } from "./-components/schedule-sidebar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/utils/hooks/use-is-mobile";

import type { SidebarState } from "./-components/schedule-sidebar";
// All legacy useState and sidebar reducer code has been removed in favor of unified reducer.
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function makeScheduleQuery(
  role: Role,
  view: "month" | "year" = "month",
  date: Date = new Date(),
) {
  const startDate = startOfMonth(date).toISOString();
  const endDate = endOfMonth(date).toISOString();
  return {
    queryKey: [QueryKeys.SCHEDULE, QueryKeys.SHIFT_LIST, role, view, startDate],
    queryFn: async (): Promise<Array<ShiftCard>> => {
      if (role === "employer") {
        const opts = view === "month" ? { startDate, endDate, page_size: 200 } : { page_size: 200 };
        const response = await getShifts(opts);
        return response.shifts.map((s) => shiftToScheduleCard(s));
      } else {
        const opts = view === "month" ? { startDate, endDate } : {};
        const response = await listWorkerShifts(opts);
        return response.assignments.map((a) => assignmentToCard(a as any));
      }
    },
  };
}

const INACTIVE_ASSIGNMENT = new Set(["CANCELLED", "NO_SHOW", "COMPLETED"]);
const ALL_FUTURE_LIMIT = 500;

/**
 * Worker "All Shifts" sidebar: every future shift regardless of the calendar month —
 * booked assignments plus offers awaiting the worker and applications awaiting approval.
 */
export function makeWorkerAllFutureQuery() {
  return {
    queryKey: [QueryKeys.SCHEDULE, "worker-all-future"],
    queryFn: async (): Promise<Array<ShiftCard>> => {
      const now = new Date();
      const [assignments, upcoming] = await Promise.all([
        listWorkerShifts({ startDate: now.toISOString(), pageSize: ALL_FUTURE_LIMIT }),
        getWorkerUpcoming(ALL_FUTURE_LIMIT),
      ]);
      const booked = assignments.assignments
        .map((a) => assignmentToCard(a as any))
        .filter(
          (c) =>
            c.startDateTime &&
            new Date(c.startDateTime) >= now &&
            !INACTIVE_ASSIGNMENT.has(c.assignmentStatus ?? ""),
        );
      const pending = upcoming.items
        .filter((u) => u.status === "offered" || u.status === "applied")
        .map(upcomingToCard);
      return [...booked, ...pending].sort(
        (a, b) =>
          new Date(a.startDateTime ?? 0).getTime() - new Date(b.startDateTime ?? 0).getTime(),
      );
    },
  };
}

export const Route = createFileRoute("/app/$role/_app/schedule/")({
  component: SchedulePage,
  staticData: {
    layout: {
      boxed: true,
      bg: "fog",
    },
  },
});

type CalendarEvent = {
  id: string;
  title: string;
  start: string;
  extendedProps: {
    worker?: string;
    business?: string;
    timeLabel?: string;
    assignmentId: string;
  };
};

function shiftsToEvents(shifts: Array<ShiftCard>): Array<CalendarEvent> {
  return shifts
    .filter((shift) => shift.startDateTime)
    .map((shift) => {
      return {
        id: shift.id,
        title: shift.name,
        start: shift.startDateTime!,
        extendedProps: {
          worker: shift.workerName,
          business: shift.businessName,
          timeLabel: shift.timeLabel,
          assignmentId: shift.id,
        },
      };
    });
}

export default function SchedulePage() {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const role = useRole();
  const calendarRef = React.useRef<any>(null);

  type ScheduleState = {
    sidebar: SidebarState;
    selectedMobileShift: ShiftCard | null;
    activeTab: "month" | "year";
    currentViewDate: Date;
  };
  type ScheduleAction =
    | { type: "SET_SIDEBAR"; sidebar: SidebarState }
    | { type: "SET_SELECTED_MOBILE_SHIFT"; shift: ShiftCard | null }
    | { type: "SET_ACTIVE_TAB"; tab: "month" | "year" }
    | { type: "SET_CURRENT_VIEW_DATE"; date: Date };

  function scheduleReducer(
    state: ScheduleState,
    action: ScheduleAction,
  ): ScheduleState {
    switch (action.type) {
      case "SET_SIDEBAR":
        return { ...state, sidebar: action.sidebar };
      case "SET_SELECTED_MOBILE_SHIFT":
        return { ...state, selectedMobileShift: action.shift };
      case "SET_ACTIVE_TAB":
        return { ...state, activeTab: action.tab };
      case "SET_CURRENT_VIEW_DATE":
        return { ...state, currentViewDate: action.date };
      default:
        return state;
    }
  }

  const [state, dispatch] = React.useReducer(scheduleReducer, {
    sidebar: { type: "list", items: [], showPast: false },
    selectedMobileShift: null,
    activeTab: "month",
    currentViewDate: new Date(),
  });

  React.useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(state.currentViewDate);
    }
  }, [state.currentViewDate, state.activeTab]);

  const { data: shiftCards } = useSuspenseQuery(
    makeScheduleQuery(role, state.activeTab, state.currentViewDate),
  );
  const events: Array<CalendarEvent> = shiftsToEvents(shiftCards);
  const { data: workerAllFuture } = useQuery({
    ...makeWorkerAllFutureQuery(),
    enabled: role === "worker",
  });

  // Worker's posted availability, keyed by YYYY-MM-DD, to render a per-day
  // indicator on the calendar.
  const { data: profileData } = useGetProfileQuery();
  const availabilityDates = React.useMemo(() => {
    if (role !== "worker") return new Set<string>();
    const windows = profileData?.profile.posted_availability ?? [];
    return new Set(windows.map((w) => w.date));
  }, [profileData, role]);

  function renderDayCellContent(arg: DayCellContentArg) {
    const isAvailable = availabilityDates.has(format(arg.date, "yyyy-MM-dd"));
    return (
      <div className="flex items-center justify-end gap-1">
        {isAvailable && (
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#2E7D32]"
            title={t("schedule.availableThisDay")}
            aria-label={t("schedule.availableThisDay")}
          />
        )}
        <span>{arg.dayNumberText}</span>
      </div>
    );
  }

  function renderAvailabilityLegend() {
    if (role !== "worker") return null;
    return (
      <div className="flex items-center justify-end gap-1.5 px-4 mt-2">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2E7D32]" />
        <span className="text-[12px] leading-none text-slate">
          {t("schedule.availableLegend")}
        </span>
      </div>
    );
  }

  const now = new Date();
  // Workers: every future shift (all months). Employers: the next 7 in the visible month.
  const upcoming: Array<ShiftCard> =
    role === "worker"
      ? (workerAllFuture ?? [])
      : shiftCards
          .filter((s) => s.startDateTime && new Date(s.startDateTime) >= now)
          .sort(
            (a, b) =>
              new Date(a.startDateTime ?? 0).getTime() -
              new Date(b.startDateTime ?? 0).getTime(),
          )
          .slice(0, 7);
  const past: Array<ShiftCard> = shiftCards
    .filter((s) => s.startDateTime && new Date(s.startDateTime) < now)
    .sort(
      (a, b) =>
        new Date(b.startDateTime ?? 0).getTime() -
        new Date(a.startDateTime ?? 0).getTime(),
    );

  React.useEffect(() => {
    if (state.sidebar.type === "list") {
      dispatch({
        type: "SET_SIDEBAR",
        sidebar: { type: "list", items: upcoming, showPast: false },
      });
    }
  }, [upcoming.length, workerAllFuture, state.sidebar.type]);

  function handleSeePast(): void {
    dispatch({
      type: "SET_SIDEBAR",
      sidebar: { type: "past", items: past },
    });
  }

  function handleSelectShift(shift: ShiftCard): void {
    dispatch({ type: "SET_SIDEBAR", sidebar: { type: "detail", shift } });
  }

  function handleBack(): void {
    dispatch({
      type: "SET_SIDEBAR",
      sidebar: { type: "list", items: upcoming, showPast: false },
    });
  }

  function handleCalendarDayClick(event: EventApi) {
    const assignmentId = event.extendedProps.assignmentId;
    const shift = shiftCards.find((s) => s.id === assignmentId);
    if (shift) {
      if (isMobile) {
        dispatch({ type: "SET_SELECTED_MOBILE_SHIFT", shift });
      } else {
        dispatch({ type: "SET_SIDEBAR", sidebar: { type: "detail", shift } });
      }
    }
  }

  function goNext(): void {
    dispatch({
      type: "SET_CURRENT_VIEW_DATE",
      date:
        state.activeTab === "month"
          ? addMonths(state.currentViewDate, 1)
          : addMonths(state.currentViewDate, 12),
    });
  }
  function goPrev(): void {
    dispatch({
      type: "SET_CURRENT_VIEW_DATE",
      date:
        state.activeTab === "month"
          ? subMonths(state.currentViewDate, 1)
          : subMonths(state.currentViewDate, 12),
    });
  }

  function renderEventContent({ event }: { event: EventApi }) {
    const name =
      role === "worker"
        ? event.extendedProps.business
        : event.extendedProps.worker ?? event.title;
    if (isMobile) {
      return (
        <div className="w-full truncate bg-[#FBF1DC] rounded-sm px-1 py-[1px] text-[9px] font-bold leading-tight text-center">
          {name || event.title}
        </div>
      );
    }
    return (
      <>
        <img
          src="/newlogo.svg"
          width="20"
          height="20"
          className="absolute -top-[22px] left-0"
        />
        <div className="flex flex-col gap-3 h-[60px] top-5 items-center">
          <span className="w-full text-center body-xs text-[10px] font-bold">
            {name}
          </span>
          <div className="w-full justify-center bg-[#FBF1DC] rounded-sm body-xs leading-[135%] text-[8px] font-bold text-center h-[18px] flex items-center">
            {event.extendedProps.timeLabel}
          </div>
        </div>
      </>
    );
  }

  const prevMonth = format(subMonths(state.currentViewDate, 1), "MMM");
  const nextMonth = format(addMonths(state.currentViewDate, 1), "MMM");
  const prevYear = (state.currentViewDate.getFullYear() - 1).toString();
  const nextYear = (state.currentViewDate.getFullYear() + 1).toString();

  if (isMobile) {
    return (
      <div className="w-full h-full bg-white p-2 fc-mobile">
        <Tabs
          value={state.activeTab}
          onValueChange={(value) =>
            dispatch({ type: "SET_ACTIVE_TAB", tab: value as "month" | "year" })
          }
          className="flex-1"
        >
          <TabsList
            className="boder-0 rounded-none w-full px-4 pt-4 pb-0 bg-transparent border-b-2"
            style={{ borderColor: "rgba(84, 90, 99, 0.25)" }}
          >
            <TabsTrigger value="month" className="body-small text-slate!">
              {t("schedule.month")}
            </TabsTrigger>
            <TabsTrigger value="year" className="body-small text-slate!">
              {t("schedule.year")}
            </TabsTrigger>
          </TabsList>
          <div className="w-full flex flex-row items-center justify-between mt-3 px-2">
            <Button
              variant="outline"
              className="h-9 w-[90px] text-[14px] leading-none gap-2 [&>svg]:w-5 [&>svg]:h-2.5 [&>svg]:shrink-0"
              onClick={goPrev}
            >
              <LeftArrow />
              <span>{state.activeTab === "month" ? prevMonth : prevYear}</span>
            </Button>
            <h2 className="text-[16px] font-semibold leading-none text-garnet">
              {state.activeTab === "month"
                ? format(state.currentViewDate, "MMM yyyy")
                : format(state.currentViewDate, "yyyy")}
            </h2>
            <Button
              variant="outline"
              className="h-9 w-[90px] text-[14px] leading-none gap-2 [&>svg]:w-5 [&>svg]:h-2.5 [&>svg]:shrink-0"
              onClick={goNext}
            >
              <span>{state.activeTab === "month" ? nextMonth : nextYear}</span>
              <RightArrow />
            </Button>
          </div>
          {renderAvailabilityLegend()}
          <TabsContent value="month" className="p-0">
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              events={events}
              height="auto"
              dayCellContent={renderDayCellContent}
              headerToolbar={{ left: "", center: "", right: "" }}
              footerToolbar={{ left: "", right: "" }}
              fixedWeekCount={false}
              dayMaxEvents
              dayMaxEventRows={2}
              nowIndicator
              expandRows={false}
              eventContent={renderEventContent}
              selectable
              ref={calendarRef}
              eventClick={(info) => handleCalendarDayClick(info.event)}
            />
          </TabsContent>
          <TabsContent value="year" className="p-0">
            <FullCalendar
              plugins={[multiMonthPlugin]}
              initialView="multiMonthYear"
              initialDate={state.currentViewDate}
              events={events}
              height="auto"
              multiMonthMaxColumns={1}
              dayCellContent={renderDayCellContent}
              multiMonthMinWidth={280}
              headerToolbar={{ left: "", center: "", right: "" }}
              footerToolbar={{ left: "", right: "" }}
              fixedWeekCount={false}
              dayMaxEvents
              dayMaxEventRows={2}
              nowIndicator
              expandRows={false}
              selectable
              ref={calendarRef}
              eventClick={(info) => handleCalendarDayClick(info.event)}
            />
          </TabsContent>
        </Tabs>
        <Sheet
          open={!!state.selectedMobileShift}
          onOpenChange={(open) => {
            if (!open)
              dispatch({ type: "SET_SELECTED_MOBILE_SHIFT", shift: null });
          }}
        >
          <SheetContent
            side="bottom"
            className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-6"
          >
            {state.selectedMobileShift && (
              <ScheduleSidebar
                state={{ type: "detail", shift: state.selectedMobileShift }}
                onBack={() =>
                  dispatch({ type: "SET_SELECTED_MOBILE_SHIFT", shift: null })
                }
                onSelectShift={() => {}}
                onSeePast={() => {}}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-row gap-6">
      <ScheduleSidebar
        state={state.sidebar}
        onSelectShift={handleSelectShift}
        onBack={handleBack}
        onSeePast={handleSeePast}
      />
      <div className="w-full bg-white p-5 rounded-[15px] min-w-[800px]">
        <Tabs
          value={state.activeTab}
          onValueChange={(value) =>
            dispatch({ type: "SET_ACTIVE_TAB", tab: value as "month" | "year" })
          }
          className="flex-1"
        >
          <TabsList
            className="boder-0 rounded-none w-full px-4 pt-4 pb-0 bg-transparent border-b-2"
            style={{ borderColor: "rgba(84, 90, 99, 0.25)" }}
          >
            <TabsTrigger value="month" className="body-small text-slate!">
              {t("schedule.month")}
            </TabsTrigger>
            <TabsTrigger value="year" className="body-small text-slate!">
              {t("schedule.year")}
            </TabsTrigger>
          </TabsList>
          <div className="w-full flex flex-row items-center justify-between mt-4 px-4">
            <Button
              variant="outline"
              className="h-9 w-[100px] text-[14px] leading-none gap-2 [&>svg]:w-5 [&>svg]:h-2.5 [&>svg]:shrink-0"
              onClick={goPrev}
            >
              <LeftArrow />
              <span>{state.activeTab === "month" ? prevMonth : prevYear}</span>
            </Button>
            <h2 className="text-[18px] font-semibold leading-none text-garnet">
              {state.activeTab === "month"
                ? format(state.currentViewDate, "MMMM yyyy")
                : format(state.currentViewDate, "yyyy")}
            </h2>
            <Button
              variant="outline"
              className="h-9 w-[100px] text-[14px] leading-none gap-2 [&>svg]:w-5 [&>svg]:h-2.5 [&>svg]:shrink-0"
              onClick={goNext}
            >
              <span>{state.activeTab === "month" ? nextMonth : nextYear}</span>
              <RightArrow />
            </Button>
          </div>
          {renderAvailabilityLegend()}
          <TabsContent value="month" className="p-0">
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              initialDate={state.currentViewDate}
              events={events}
              height={940}
              dayCellContent={renderDayCellContent}
              headerToolbar={{ left: "", center: "", right: "" }}
              footerToolbar={{ left: "", right: "" }}
              fixedWeekCount={false}
              dayMaxEvents
              dayMaxEventRows={2}
              nowIndicator
              expandRows={false}
              eventContent={renderEventContent}
              selectable
              ref={calendarRef}
              eventClick={(info) => handleCalendarDayClick(info.event)}
            />
          </TabsContent>
          <TabsContent value="year" className="p-0">
            <FullCalendar
              plugins={[multiMonthPlugin]}
              initialView="multiMonthYear"
              initialDate={state.currentViewDate}
              events={events}
              eventContent={renderEventContent}
              height={940}
              dayCellContent={renderDayCellContent}
              headerToolbar={{ left: "", center: "", right: "" }}
              footerToolbar={{ left: "", right: "" }}
              fixedWeekCount={false}
              dayMaxEvents
              dayMaxEventRows={2}
              nowIndicator
              expandRows={false}
              selectable
              ref={calendarRef}
              eventClick={(info) => handleCalendarDayClick(info.event)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
